import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { fetchJson } from "../api/rest";
import {
	btn,
	ErrorBanner,
	fmtDateTime,
	fmtNumber,
	kr,
	Panel,
	Spinner,
} from "../components/flows/ui";
import { PageHeader } from "../components/layout/PageHeader";
import type {
	ChannelReport,
	PeriodKey,
	PeriodRange,
	PeriodTotals,
	RevenueReport,
} from "../types/revenue";

const CHANNEL_COLORS: Record<string, string> = {
	partnerads: "#2563eb",
	adtraction: "#f59e0b",
};

const fmtShortDate = (iso: string) =>
	`${Number(iso.slice(8, 10))}/${Number(iso.slice(5, 7))}`;

const projected = (totals: PeriodTotals, period: PeriodRange) =>
	period.elapsedDays > 0 ? (totals.net / period.elapsedDays) * period.days : 0;

const delta = (current: number, previous: number) => {
	if (previous === 0) return null;
	return ((current - previous) / Math.abs(previous)) * 100;
};

const DeltaChip = ({ value }: { value: number | null }) => {
	if (value === null || !Number.isFinite(value)) return null;
	const up = value >= 0;
	return (
		<span
			className={`ml-2 rounded px-1.5 py-0.5 text-[11px] font-medium ${up ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
		>
			{up ? "+" : ""}
			{value.toFixed(0)}%
		</span>
	);
};

const PeriodCard = ({
	period,
	totals,
	report,
}: {
	period: PeriodRange;
	totals: PeriodTotals;
	report: RevenueReport;
}) => {
	const last = report.totals.lastMonth;
	const lastPeriod = report.periods.find((p) => p.key === "lastMonth");
	const isCurrent = period.key === "currentMonth";
	const forecast = isCurrent ? projected(totals, period) : null;
	return (
		<div className="card bg-white p-5">
			<div className="text-[12px] font-medium uppercase tracking-wide text-slate-500">
				{period.label}
			</div>
			<div className="mt-0.5 text-[11px] text-slate-400">
				{period.from} → {period.to}
			</div>
			<div className="mt-3 flex items-baseline text-2xl font-semibold text-slate-800">
				{kr(totals.net)}
				{isCurrent && forecast !== null && (
					<DeltaChip value={delta(forecast, last.net)} />
				)}
				{period.key === "last30Days" && lastPeriod && (
					<DeltaChip
						value={delta(totals.net, (last.net / lastPeriod.days) * 30)}
					/>
				)}
			</div>
			<div className="text-[11px] text-slate-400">net commission</div>
			<dl className="mt-4 grid grid-cols-2 gap-y-1 text-[12px]">
				<dt className="text-slate-500">Sales</dt>
				<dd className="text-right text-slate-700">{fmtNumber(totals.sales)}</dd>
				<dt className="text-slate-500">Order value</dt>
				<dd className="text-right text-slate-700">{kr(totals.orderValue)}</dd>
				<dt className="text-slate-500">Pending</dt>
				<dd className="text-right text-slate-700">{kr(totals.pending)}</dd>
				<dt className="text-slate-500">Cancelled</dt>
				<dd className="text-right text-slate-700">{kr(totals.cancelled)}</dd>
				<dt className="text-slate-500">Per day</dt>
				<dd className="text-right text-slate-700">
					{kr(period.elapsedDays ? totals.net / period.elapsedDays : 0)}
				</dd>
				{forecast !== null && (
					<>
						<dt className="text-slate-500">Month forecast</dt>
						<dd className="text-right font-medium text-slate-700">
							{kr(forecast)}
						</dd>
					</>
				)}
			</dl>
		</div>
	);
};

const COLUMNS: Array<{
	label: string;
	value: (t: PeriodTotals) => string;
}> = [
	{ label: "Clicks", value: (t) => fmtNumber(t.clicks) },
	{ label: "Sales", value: (t) => fmtNumber(t.sales) },
	{ label: "Order value", value: (t) => kr(t.orderValue) },
	{ label: "Commission", value: (t) => kr(t.commission) },
	{ label: "Pending", value: (t) => kr(t.pending) },
	{ label: "Cancelled", value: (t) => kr(t.cancelled) },
	{ label: "Net", value: (t) => kr(t.net) },
	{
		label: "EPC",
		value: (t) =>
			t.clicks
				? `${(t.net / t.clicks).toLocaleString("da-DK", { maximumFractionDigits: 2 })} kr`
				: "—",
	},
];

const ChannelTable = ({
	report,
	periodKey,
}: {
	report: RevenueReport;
	periodKey: PeriodKey;
}) => {
	const rows: Array<{
		name: string;
		totals: PeriodTotals;
		channel?: ChannelReport;
		total?: boolean;
	}> = [
		...report.channels.map((c) => ({
			name: c.name,
			totals: c.periods[periodKey],
			channel: c,
		})),
		{ name: "Total", totals: report.totals[periodKey], total: true },
	];
	return (
		<div className="overflow-x-auto">
			<table className="w-full text-[13px]">
				<thead>
					<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-slate-500">
						<th className="py-2 pr-4 font-medium">Channel</th>
						{COLUMNS.map((c) => (
							<th key={c.label} className="py-2 pl-4 text-right font-medium">
								{c.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr
							key={row.name}
							className={`border-b border-hairline last:border-0 ${row.total ? "font-semibold" : ""}`}
						>
							<td className="py-2 pr-4">
								<span className="inline-flex items-center gap-2">
									{row.channel && (
										<span
											className="h-2.5 w-2.5 rounded-sm"
											style={{ background: CHANNEL_COLORS[row.channel.key] }}
										/>
									)}
									{row.name}
									{row.channel?.error && (
										<span className="text-[11px] font-normal text-danger">
											unavailable
										</span>
									)}
								</span>
							</td>
							{COLUMNS.map((c) => (
								<td key={c.label} className="py-2 pl-4 text-right tabular-nums">
									{row.channel?.error ? "—" : c.value(row.totals)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

const RevenueChannels = () => {
	const [report, setReport] = useState<RevenueReport | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [periodKey, setPeriodKey] = useState<PeriodKey>("currentMonth");

	const load = useCallback(async (refresh: boolean) => {
		setLoading(true);
		setError(null);
		try {
			setReport(
				await fetchJson<RevenueReport>(
					`/revenue/channels${refresh ? "?refresh=1" : ""}`,
				),
			);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load(false);
	}, [load]);

	const chartData = useMemo(() => {
		if (!report) return [];
		const byDate = new Map<string, Record<string, number | string>>();
		for (const channel of report.channels) {
			for (const day of channel.days) {
				const row = byDate.get(day.date) ?? { date: day.date };
				row[channel.key] =
					Math.round((day.commission - day.cancelled) * 100) / 100;
				byDate.set(day.date, row);
			}
		}
		return [...byDate.values()].sort((a, b) =>
			String(a.date).localeCompare(String(b.date)),
		);
	}, [report]);

	const failed = report?.channels.filter((c) => c.error) ?? [];

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Menu · Revenue Channels"
				title="Revenue Channels."
				qualifier="Commission, read live."
				description={
					<p>
						Affiliate commission from Partner-ads and Adtraction, read live from
						each network. Net is commission minus cancellations;
						Adtraction&apos;s pending commission is included until it is
						approved or rejected.
					</p>
				}
				actions={
					<>
						{report && (
							<span className="text-[11px] text-zinc-400">
								Updated {fmtDateTime(report.generatedAt)}
							</span>
						)}
						<button
							type="button"
							className={btn.ghost}
							disabled={loading}
							onClick={() => load(true)}
						>
							<RefreshCw
								className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
							/>
							Refresh
						</button>
					</>
				}
			/>

			<ErrorBanner error={error} />
			{failed.map((c) => (
				<ErrorBanner key={c.key} error={`${c.name}: ${c.error}`} />
			))}

			{!report && loading && <Spinner />}

			{report && (
				<>
					<div className="grid gap-4 md:grid-cols-3">
						{report.periods.map((p) => (
							<PeriodCard
								key={p.key}
								period={p}
								totals={report.totals[p.key]}
								report={report}
							/>
						))}
					</div>

					<Panel
						title="By channel"
						action={
							<div className="flex gap-1">
								{report.periods.map((p) => (
									<button
										key={p.key}
										type="button"
										onClick={() => setPeriodKey(p.key)}
										className={`rounded-md px-2.5 py-1 text-[12px] font-medium ${periodKey === p.key ? "bg-primary text-white" : "text-slate-600 hover:bg-canvas"}`}
									>
										{p.label}
									</button>
								))}
							</div>
						}
					>
						<ChannelTable report={report} periodKey={periodKey} />
					</Panel>

					<Panel
						title="Net commission per day"
						hint={`${report.periods.find((p) => p.key === "lastMonth")?.from} → ${report.today}`}
					>
						<div className="h-72">
							<ResponsiveContainer width="100%" height="100%">
								<BarChart data={chartData}>
									<CartesianGrid
										strokeDasharray="3 3"
										vertical={false}
										stroke="#e5e7eb"
									/>
									<XAxis
										dataKey="date"
										tickFormatter={fmtShortDate}
										tick={{ fontSize: 11 }}
										minTickGap={12}
									/>
									<YAxis tick={{ fontSize: 11 }} width={56} />
									<Tooltip
										formatter={(value: number) => kr(value)}
										labelFormatter={(label: string) => label}
									/>
									<Legend wrapperStyle={{ fontSize: 12 }} />
									{report.channels.map((c) => (
										<Bar
											key={c.key}
											dataKey={c.key}
											name={c.name}
											stackId="net"
											fill={CHANNEL_COLORS[c.key]}
										/>
									))}
								</BarChart>
							</ResponsiveContainer>
						</div>
					</Panel>
				</>
			)}
		</div>
	);
};

export default RevenueChannels;
